import type {
  Modification,
  ModificationGroupLimits,
  ModificationsMap,
  PairedModifierGroupMap,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per paired group, assigns each swappable ingredient to the INCLUDED or EXTRAS
 * side using intrinsic priority keys only, so the same final selection produces
 * the same modification list regardless of the order the user clicked items in.
 *
 * For each ingredient the quantity that matches the initial recipe is pinned
 * on the included side and never displaced. The remaining "flexible" quantity
 * competes for the leftover included slots. Priority for flexible units:
 *   1. Ingredient was in the initial recipe
 *   2. Bigger `extrasCost - includedCost` savings
 *   3. Lower `order` within the paired included group
 *   4. Lower ingredientId (lexicographic)
 *
 * Ingredients that aren't swappable (only one side exists in the product) pass
 * through unchanged.
 */
export function optimizeIncludedSlots(params: Params): Modification[] {
  const {
    activeModifications,
    initialModifications,
    includedModificationsMap,
    extrasModificationsMap,
    includedModifierGroupsByExtrasName,
    modificationLimits,
  } = params;

  const pairings = buildPairings(includedModifierGroupsByExtrasName);

  if (pairings.length === 0) return activeModifications;

  const pairingByGroupName = buildPairingByGroupName(pairings);

  const planByKey = pairings.reduce<Map<string, PairingPlan>>((plans, pairing) => {
    const plan = buildPairingPlan({
      pairing,
      activeModifications,
      initialModifications,
      includedModificationsMap,
      extrasModificationsMap,
      modificationLimits,
    });

    plans.set(pairing.key, plan);

    return plans;
  }, new Map());

  const output = applyPlans({
    activeModifications,
    planByKey,
    pairingByGroupName,
    includedModificationsMap,
    extrasModificationsMap,
  });

  if (checkIfUnchanged(output, activeModifications)) return activeModifications;

  return output;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildPairings(pairedGroupMap: PairedModifierGroupMap): Pairing[] {
  return Object.entries(pairedGroupMap)
    .map(([extrasName, includedName]) => ({
      extrasName,
      includedName,
      key: `${includedName}|${extrasName}`,
    }))
    .sort(comparePairings);
}

function comparePairings(currentPairing: Pairing, nextPairing: Pairing) {
  return currentPairing.key.localeCompare(nextPairing.key);
}

function buildPairingByGroupName(pairings: Pairing[]) {
  return pairings.reduce<Map<string, Pairing>>((byName, pairing) => {
    byName.set(pairing.includedName, pairing);
    byName.set(pairing.extrasName, pairing);

    return byName;
  }, new Map());
}

// ─────────────────────────────────────────────────────────────────────────────

function buildPairingPlan(params: BuildPairingPlanParams): PairingPlan {
  const {
    pairing,
    activeModifications,
    initialModifications,
    includedModificationsMap,
    extrasModificationsMap,
    modificationLimits,
  } = params;

  const totalUnits = aggregateUnitsInPairing({
    modifications: activeModifications,
    pairing,
    countOnlyIncluded: false,
  });
  const activeIncludedUnits = aggregateUnitsInPairing({
    modifications: activeModifications,
    pairing,
    countOnlyIncluded: true,
  });
  const initialIncludedUnits = aggregateUnitsInPairing({
    modifications: initialModifications,
    pairing,
    countOnlyIncluded: true,
  });

  const candidates = collectFlexibleCandidates({
    totalUnits,
    activeIncludedUnits,
    initialIncludedUnits,
    includedModificationsMap,
    extrasModificationsMap,
  });

  if (candidates.flexible.length === 0) {
    return { pairing, assignments: new Map() };
  }

  const maxAggregateQuantity = modificationLimits[pairing.includedName]?.maxAggregateQuantity ?? 0; // prettier-ignore
  const remainingSlots = Math.max(0, maxAggregateQuantity - candidates.reservedIncludedUnits);

  const sorted = [...candidates.flexible].sort(compareFlexibleCandidates);

  const { entries } = sorted.reduce<SlotAllocationAccumulator>(allocateSlot, {
    slotsLeft: remainingSlots,
    entries: [],
  });

  return { pairing, assignments: new Map(entries) };
}

function allocateSlot(
  accumulator: SlotAllocationAccumulator,
  candidate: FlexibleCandidate,
): SlotAllocationAccumulator {
  const toIncluded = Math.min(candidate.flexibleQuantity, accumulator.slotsLeft);
  const toExtras = candidate.flexibleQuantity - toIncluded;

  return {
    slotsLeft: accumulator.slotsLeft - toIncluded,
    entries: [
      ...accumulator.entries,
      [
        candidate.ingredientId,
        { included: candidate.pinnedIncludedQuantity + toIncluded, extras: toExtras },
      ],
    ],
  };
}

function aggregateUnitsInPairing(params: AggregateUnitsParams) {
  const { modifications, pairing, countOnlyIncluded } = params;

  return modifications.reduce<Map<string, number>>((units, modification) => {
    if (!modification.ingredientId) return units;
    if (!checkIfInPairing(modification, pairing)) return units;
    if (countOnlyIncluded && modification.isExtra) return units;

    const current = units.get(modification.ingredientId) ?? 0;
    units.set(modification.ingredientId, current + modification.quantity);

    return units;
  }, new Map());
}

function checkIfInPairing(modification: Modification, pairing: Pairing) {
  return (
    modification.modificationGroupName === pairing.includedName ||
    modification.modificationGroupName === pairing.extrasName
  );
}

function collectFlexibleCandidates(params: CollectCandidatesParams) {
  const classified = [...params.totalUnits.entries()].map(([ingredientId, totalQuantity]) =>
    classifyIngredient({ ingredientId, totalQuantity, ...params }),
  );

  return {
    flexible: classified.flatMap((entry) => (entry.type === 'flexible' ? [entry.candidate] : [])),
    reservedIncludedUnits: classified.reduce(
      (reservedUnits, entry) => reservedUnits + entry.reservedUnits,
      0,
    ),
  };
}

function classifyIngredient(params: ClassifyIngredientParams): ClassifiedIngredient {
  const {
    ingredientId,
    totalQuantity,
    activeIncludedUnits,
    initialIncludedUnits,
    includedModificationsMap,
    extrasModificationsMap,
  } = params;

  const includedCounterpart = includedModificationsMap.get(ingredientId);
  const extrasCounterpart = extrasModificationsMap.get(ingredientId);
  const activeIncludedQuantity = activeIncludedUnits.get(ingredientId) ?? 0;

  if (!includedCounterpart || !extrasCounterpart) {
    return { type: 'fixed', reservedUnits: activeIncludedQuantity };
  }

  const initialIncludedQuantity = initialIncludedUnits.get(ingredientId) ?? 0;
  const pinnedIncluded = Math.min(initialIncludedQuantity, activeIncludedQuantity);
  const flexibleQuantity = Math.max(0, totalQuantity - pinnedIncluded);

  return {
    type: 'flexible',
    reservedUnits: pinnedIncluded,
    candidate: {
      ingredientId,
      flexibleQuantity,
      pinnedIncludedQuantity: pinnedIncluded,
      isInitialIncluded: initialIncludedQuantity > 0,
      includedCounterpart,
      extrasCounterpart,
    },
  };
}

function compareFlexibleCandidates(
  currentCandidate: FlexibleCandidate,
  nextCandidate: FlexibleCandidate,
) {
  const currentInitial = currentCandidate.isInitialIncluded ? 1 : 0;
  const nextInitial = nextCandidate.isInitialIncluded ? 1 : 0;

  if (currentInitial !== nextInitial) return nextInitial - currentInitial;

  const currentSavings = currentCandidate.extrasCounterpart.additionCost - currentCandidate.includedCounterpart.additionCost; // prettier-ignore
  const nextSavings = nextCandidate.extrasCounterpart.additionCost - nextCandidate.includedCounterpart.additionCost; // prettier-ignore

  if (currentSavings !== nextSavings) return nextSavings - currentSavings;

  // `modificationGroupOrder` is the same for every candidate in a given pairing
  // (they all share the same included group), so `order` within that group is
  // the next meaningful tiebreaker.
  const currentOrder = currentCandidate.includedCounterpart.order;
  const nextOrder = nextCandidate.includedCounterpart.order;

  if (currentOrder !== nextOrder) return currentOrder - nextOrder;

  return currentCandidate.ingredientId.localeCompare(nextCandidate.ingredientId);
}

// ─────────────────────────────────────────────────────────────────────────────

function applyPlans(params: ApplyPlansParams): Modification[] {
  const { activeModifications } = params;

  return activeModifications.reduce<ApplyPlansAccumulator>(
    (accumulator, modification) => projectModification({ accumulator, modification, ...params }),
    { output: [], consumed: new Set() },
  ).output;
}

function projectModification(params: ProjectModificationParams): ApplyPlansAccumulator {
  const {
    accumulator,
    modification,
    planByKey,
    pairingByGroupName,
    includedModificationsMap,
    extrasModificationsMap,
  } = params;

  const pairing = pairingByGroupName.get(modification.modificationGroupName);

  if (!pairing || !modification.ingredientId) {
    return { output: [...accumulator.output, modification], consumed: accumulator.consumed };
  }

  const assignment = planByKey.get(pairing.key)?.assignments.get(modification.ingredientId);

  if (!assignment) {
    return { output: [...accumulator.output, modification], consumed: accumulator.consumed };
  }

  const consumedKey = `${pairing.key}|${modification.ingredientId}`;

  if (accumulator.consumed.has(consumedKey)) return accumulator;

  const entries = buildAssignedEntries({
    assignment,
    ingredientId: modification.ingredientId,
    includedModificationsMap,
    extrasModificationsMap,
  });

  return {
    output: [...accumulator.output, ...entries],
    consumed: new Set(accumulator.consumed).add(consumedKey),
  };
}

function buildAssignedEntries(params: BuildAssignedEntriesParams): Modification[] {
  const { assignment, ingredientId, includedModificationsMap, extrasModificationsMap } = params;

  const includedTemplate = includedModificationsMap.get(ingredientId);
  const extrasTemplate = extrasModificationsMap.get(ingredientId);

  const includedEntry =
    assignment.included > 0 && includedTemplate
      ? { ...includedTemplate, quantity: assignment.included }
      : null;

  const extrasEntry =
    assignment.extras > 0 && extrasTemplate
      ? { ...extrasTemplate, quantity: assignment.extras }
      : null;

  return [includedEntry, extrasEntry].filter(checkIfDefined);
}

function checkIfDefined(modification: Modification | null): modification is Modification {
  return modification !== null;
}

// ─────────────────────────────────────────────────────────────────────────────

function checkIfUnchanged(output: Modification[], activeModifications: Modification[]) {
  if (output.length !== activeModifications.length) return false;

  return output.every((modification, index) => {
    const active = activeModifications[index];

    return modification.id === active.id && modification.quantity === active.quantity;
  });
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  activeModifications: Modification[];
  initialModifications: Modification[];
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  modificationLimits: ModificationGroupLimits;
};

type Pairing = {
  includedName: string;
  extrasName: string;
  key: string;
};

type PairingAssignment = {
  included: number;
  extras: number;
};

type PairingPlan = {
  pairing: Pairing;
  assignments: Map<string, PairingAssignment>;
};

type FlexibleCandidate = {
  ingredientId: string;
  flexibleQuantity: number;
  pinnedIncludedQuantity: number;
  isInitialIncluded: boolean;
  includedCounterpart: Modification;
  extrasCounterpart: Modification;
};

type ClassifiedIngredient =
  | { type: 'fixed'; reservedUnits: number }
  | { type: 'flexible'; reservedUnits: number; candidate: FlexibleCandidate };

type ClassifyIngredientParams = {
  ingredientId: string;
  totalQuantity: number;
  activeIncludedUnits: Map<string, number>;
  initialIncludedUnits: Map<string, number>;
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
};

type SlotAllocationAccumulator = {
  slotsLeft: number;
  entries: Array<[string, PairingAssignment]>;
};

type BuildPairingPlanParams = {
  pairing: Pairing;
  activeModifications: Modification[];
  initialModifications: Modification[];
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  modificationLimits: ModificationGroupLimits;
};

type AggregateUnitsParams = {
  modifications: Modification[];
  pairing: Pairing;
  countOnlyIncluded: boolean;
};

type CollectCandidatesParams = {
  totalUnits: Map<string, number>;
  activeIncludedUnits: Map<string, number>;
  initialIncludedUnits: Map<string, number>;
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
};

type ApplyPlansParams = {
  activeModifications: Modification[];
  planByKey: Map<string, PairingPlan>;
  pairingByGroupName: Map<string, Pairing>;
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
};

type ApplyPlansAccumulator = {
  output: Modification[];
  consumed: Set<string>;
};

type ProjectModificationParams = {
  accumulator: ApplyPlansAccumulator;
  modification: Modification;
  planByKey: Map<string, PairingPlan>;
  pairingByGroupName: Map<string, Pairing>;
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
};

type BuildAssignedEntriesParams = {
  assignment: PairingAssignment;
  ingredientId: string;
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
};
